/**
 * Material Detail Page - Luxury Tech Edition
 * ===========================================
 * SlabReality Material Intelligence Platform
 * 
 * Design Language:
 * - Dark cinematic UI (#050505 / #0A0A0A)
 * - Warm gold accents (#C8A36A)
 * - Editorial serif typography
 * - Apple + Archiproducts aesthetic
 * - AI Material Intelligence focus
 * 
 * @author SlabReality Team
 * @version 4.0 - Luxury Tech Redesign
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Heart, Share2, MapPin, MessageCircle, Camera,
  ChevronLeft, ChevronRight, Maximize2, Play, Layers, Sparkles,
  Thermometer, Activity, Droplets, Zap, Eye, Sun, Grid3X3,
  Waves, Mountain, Diamond, Palette, Target, BarChart3,
  Building2, Users, Package, Truck, Phone, Mail, ExternalLink,
  Compass, TrendingUp, Columns, LayoutGrid, Home, Store
} from 'lucide-react';
import { getMaterialById } from '../services/materialsApi';
import { getMaterialImageUrl, getMaterialImages, handleImageError, PLACEHOLDER_IMAGE } from '../utils/materialImages';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import axios from 'axios';
import { AdvisorSection } from '../components/advisor/AdvisorIntelligenceCard';

const API = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// DNA METRIC BAR COMPONENT
// =============================================================================
const DNAMetricBar = ({ label, value, maxValue = 100, icon: Icon, delay = 0 }) => {
  const [animated, setAnimated] = useState(false);
  const percentage = Math.min((value / maxValue) * 100, 100);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#C8A36A]/70" />}
          <span className="text-xs uppercase tracking-wider text-[#F5F1E8]/60">{label}</span>
        </div>
        <span className="text-sm font-medium text-[#F5F1E8]">{value}%</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ 
            width: animated ? `${percentage}%` : '0%',
            background: 'linear-gradient(90deg, #C8A36A 0%, #D4AF37 50%, #C8A36A 100%)',
            boxShadow: '0 0 10px rgba(200, 163, 106, 0.4)'
          }}
        />
      </div>
    </div>
  );
};

// =============================================================================
// DNA CARD COMPONENT
// =============================================================================
const DNACard = ({ title, value, subtitle, icon: Icon }) => (
  <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-4 hover:border-[#C8A36A]/30 transition-all duration-300 group">
    <div className="flex items-start justify-between mb-3">
      {Icon && (
        <div className="w-8 h-8 rounded-lg bg-[#C8A36A]/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#C8A36A]" />
        </div>
      )}
    </div>
    <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/50 mb-1">{title}</div>
    <div className="text-lg font-medium text-[#F5F1E8] group-hover:text-[#C8A36A] transition-colors">{value}</div>
    {subtitle && <div className="text-xs text-[#F5F1E8]/40 mt-1">{subtitle}</div>}
  </div>
);

// =============================================================================
// COLOR SWATCH COMPONENT
// =============================================================================
const ColorSwatch = ({ color, label, isMain }) => (
  <div className="flex flex-col items-center gap-2">
    <div 
      className={`rounded-full border-2 transition-all duration-300 ${isMain ? 'w-12 h-12 border-[#C8A36A]' : 'w-8 h-8 border-white/10 hover:border-[#C8A36A]/50'}`}
      style={{ backgroundColor: color }}
    />
    {label && <span className="text-[10px] uppercase tracking-wider text-[#F5F1E8]/50">{label}</span>}
  </div>
);

// =============================================================================
// ADVISOR CARD COMPONENT
// =============================================================================
const AdvisorCard = ({ name, role, distance, image, onClick }) => (
  <div 
    onClick={onClick}
    className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/5 hover:border-[#C8A36A]/30 cursor-pointer transition-all duration-300 group"
  >
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C8A36A]/20 to-[#C8A36A]/5 flex items-center justify-center overflow-hidden">
      {image ? (
        <img src={image} alt={name} className="w-full h-full object-cover" />
      ) : (
        <Users className="w-5 h-5 text-[#C8A36A]" />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-[#F5F1E8] truncate group-hover:text-[#C8A36A] transition-colors">{name}</div>
      <div className="text-xs text-[#F5F1E8]/50">{role}</div>
    </div>
    {distance && (
      <div className="text-xs text-[#C8A36A]">{distance}</div>
    )}
  </div>
);

// =============================================================================
// VEIN INTELLIGENCE COMPONENTS
// =============================================================================

// Visual Energy Scale
const VisualEnergyScale = ({ level }) => {
  const levels = ['Calm', 'Balanced', 'Dynamic', 'Dramatic'];
  const levelIndex = levels.findIndex(l => l.toLowerCase() === level?.toLowerCase()) ?? 1;
  
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/50">Visual Energy</div>
      <div className="flex items-center gap-1">
        {levels.map((l, i) => (
          <div key={l} className="flex-1 flex flex-col items-center gap-2">
            <div 
              className={`h-2 w-full rounded-full transition-all duration-500 ${
                i <= levelIndex 
                  ? 'bg-gradient-to-r from-[#C8A36A] to-[#D4AF37]' 
                  : 'bg-white/10'
              }`}
              style={{
                opacity: i <= levelIndex ? 1 - (levelIndex - i) * 0.2 : 0.3,
                boxShadow: i === levelIndex ? '0 0 10px rgba(200, 163, 106, 0.5)' : 'none'
              }}
            />
            <span className={`text-[10px] uppercase tracking-wider transition-colors ${
              i === levelIndex ? 'text-[#C8A36A]' : 'text-[#F5F1E8]/30'
            }`}>
              {l}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Vein Direction Indicator
const VeinDirectionIndicator = ({ angle, confidence, label }) => {
  const normalizedAngle = angle || 0;
  
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-16 h-16">
        {/* Compass circle */}
        <div className="absolute inset-0 rounded-full border border-[#C8A36A]/20" />
        <div className="absolute inset-2 rounded-full border border-[#C8A36A]/10" />
        
        {/* Direction arrow */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ transform: `rotate(${normalizedAngle}deg)` }}
        >
          <div className="w-0.5 h-6 bg-gradient-to-t from-transparent via-[#C8A36A] to-[#D4AF37] rounded-full" />
          <div 
            className="absolute top-2 w-2 h-2 border-t-2 border-r-2 border-[#C8A36A]"
            style={{ transform: 'rotate(-45deg)' }}
          />
        </div>
        
        {/* Center dot */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-[#C8A36A]" />
        </div>
      </div>
      
      <div>
        <div className="text-lg font-medium text-[#F5F1E8]">{label || 'Mixed'}</div>
        <div className="text-xs text-[#F5F1E8]/50">{Math.round(confidence || 0)}% confidence</div>
      </div>
    </div>
  );
};

// Continuity Score Card
const ContinuityScoreCard = ({ title, score, recommendation, icon: Icon }) => {
  const getScoreColor = (s) => {
    if (s >= 75) return 'text-green-400';
    if (s >= 50) return 'text-[#C8A36A]';
    return 'text-red-400';
  };
  
  const getScoreLabel = (s) => {
    if (s >= 75) return 'Excellent';
    if (s >= 50) return 'Good';
    if (s >= 25) return 'Fair';
    return 'Low';
  };
  
  return (
    <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#C8A36A]" />}
          <span className="text-xs uppercase tracking-wider text-[#F5F1E8]/50">{title}</span>
        </div>
        <div className={`text-2xl font-serif ${getScoreColor(score)}`}>{Math.round(score)}%</div>
      </div>
      <div className={`text-sm font-medium mb-2 ${getScoreColor(score)}`}>{getScoreLabel(score)} Potential</div>
      <p className="text-xs text-[#F5F1E8]/50 leading-relaxed">{recommendation}</p>
    </div>
  );
};

// Application Recommendation Badge
const ApplicationBadge = ({ label, recommended }) => (
  <div className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
    recommended 
      ? 'bg-[#C8A36A]/20 text-[#C8A36A] border border-[#C8A36A]/30' 
      : 'bg-white/5 text-[#F5F1E8]/40 border border-white/5'
  }`}>
    {label}
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const MaterialDetailLuxury = () => {
  const { materialId } = useParams();
  const id = materialId;
  const navigate = useNavigate();
  
  // State
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [dnaAnalysis, setDnaAnalysis] = useState(null);
  const [dnaLoading, setDnaLoading] = useState(false);
  const [veinAnalysis, setVeinAnalysis] = useState(null);
  const [veinLoading, setVeinLoading] = useState(false);
  const [imageHover, setImageHover] = useState(false);
  const [matchedAdvisors, setMatchedAdvisors] = useState([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  
  const heroRef = useRef(null);
  
  // Load material
  useEffect(() => {
    const loadMaterial = async () => {
      setLoading(true);
      try {
        const res = await getMaterialById(id);
        if (res.success) {
          setMaterial(res.data);
          // Use DNA data directly from database if available
          if (res.data.dna || res.data.material_dna?.[0]) {
            const dbDna = res.data.dna || res.data.material_dna?.[0];
            // Map database DNA fields to component expected format
            setDnaAnalysis({
              // Basic info
              dominant_tone: dbDna.tone || dbDna.color_family || 'N/A',
              temperature: dbDna.tone === 'warm' ? 'Caldo' : dbDna.tone === 'cool' ? 'Freddo' : 'Neutro',
              vein_movement_type: dbDna.pattern_type === 'veined' ? 'Venato' : dbDna.pattern_type === 'uniform' ? 'Uniforme' : dbDna.pattern_type || 'Misto',
              crystal_presence: dbDna.crystallization_score > 70 ? 'Alto' : dbDna.crystallization_score > 40 ? 'Medio' : 'Basso',
              material_mood: dbDna.visual_intensity === 'high' ? 'Drammatico' : dbDna.visual_intensity === 'low' ? 'Calmo' : 'Equilibrato',
              surface_energy: dbDna.visual_intensity === 'high' ? 'Alta' : dbDna.visual_intensity === 'low' ? 'Bassa' : 'Media',
              // Scores for metric bars
              vein_movement: (dbDna.movement_intensity || 0) * 20, // Scale 1-5 to 0-100
              busy_level: dbDna.busy_score || 0,
              crystalization: dbDna.crystallization_score || 0,
              contrast_energy: dbDna.contrast === 'high' ? 80 : dbDna.contrast === 'low' ? 30 : 55,
              warmth: dbDna.warmth_score || 0,
              // Additional scores
              elegance: dbDna.elegance_score || 0,
              luxury_index: dbDna.luxury_index || 0,
              // Palette placeholder
              palette: ['#C8A36A', '#8B7355', '#D4AF37', '#A0522D', '#DEB887'],
            });
            setVeinAnalysis({
              direction: dbDna.vein_direction || 'mixed',
              direction_angle: dbDna.vein_direction === 'horizontal' ? 0 : dbDna.vein_direction === 'vertical' ? 90 : dbDna.vein_direction === 'diagonal' ? 45 : 30,
              direction_label: dbDna.vein_direction === 'horizontal' ? 'Orizzontale' : dbDna.vein_direction === 'vertical' ? 'Verticale' : dbDna.vein_direction === 'diagonal' ? 'Diagonale' : 'Complesso',
              confidence: dbDna.flow_confidence || 60,
              visual_energy: dbDna.visual_intensity === 'high' ? 'Dramatic' : dbDna.visual_intensity === 'low' ? 'Calm' : 'Balanced',
              continuity: dbDna.continuity_score || 0,
              waterfall_potential: dbDna.waterfall_score || 0,
              bookmatch_potential: dbDna.bookmatch_score || 0,
              movement_intensity: (dbDna.movement_intensity || 3) * 20,
              vein_density: (dbDna.vein_density || 3) * 20,
            });
            setDnaLoading(false);
            setVeinLoading(false);
          } else {
            // No DNA in database - try real-time analysis as fallback
            analyzeMaterialDNA(res.data);
          }
        } else {
          setError(res.error || 'Material not found');
        }
      } catch (err) {
        setError('Failed to load material');
      }
      setLoading(false);
    };
    
    loadMaterial();
  }, [id]);
  
  // Load matched advisors for this material
  useEffect(() => {
    const loadAdvisors = async () => {
      if (!material) return;
      
      setAdvisorsLoading(true);
      try {
        const category = material.stone_type || material.category || '';
        const response = await axios.get(
          `${API}/api/ail/advisors/for-material/${encodeURIComponent(material.id || material.name)}`,
          { params: { material_category: category, limit: 5 } }
        );
        
        if (response.data?.advisors) {
          setMatchedAdvisors(response.data.advisors);
        }
      } catch (err) {
        console.warn('[AIL] Failed to load matched advisors:', err);
        // Silent fail - advisors are supplementary content
      }
      setAdvisorsLoading(false);
    };
    
    loadAdvisors();
  }, [material]);
  
  // Analyze Material DNA
  const analyzeMaterialDNA = async (mat) => {
    if (!mat?.image_main && !mat?.image_url) return;
    
    setDnaLoading(true);
    setVeinLoading(true);
    
    // Set max timeout for analysis - NEVER infinite spinner
    const ANALYSIS_TIMEOUT = 8000; // 8 seconds max
    const timeoutId = setTimeout(() => {
      if (dnaLoading || veinLoading) {
        setDnaLoading(false);
        setVeinLoading(false);
        console.warn('[DNA] Analysis timeout - using fallback data');
      }
    }, ANALYSIS_TIMEOUT);
    
    try {
      // Get image as base64
      const imageUrl = mat.image_main || mat.image_url;
      const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`;
      
      const imgResponse = await fetch(fullUrl);
      const blob = await imgResponse.blob();
      const reader = new FileReader();
      
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        // Run combined DNA + Vein analysis
        try {
          const response = await axios.post(`${API}/api/material-dna/combined-analysis`, {
            image_base64: base64,
            include_matches: false
          }, { timeout: 7000 }); // API timeout slightly less than UI timeout
          
          if (response.data.success !== false) {
            // Set DNA analysis
            if (response.data.dna) {
              setDnaAnalysis(response.data.dna);
            }
            // Set Vein analysis
            if (response.data.vein_analysis) {
              setVeinAnalysis(response.data.vein_analysis);
            }
          }
        } catch (err) {
          console.error('Combined analysis error:', err);
          // Fallback to separate DNA analysis
          try {
            const dnaRes = await axios.post(`${API}/api/material-dna/analyze`, {
              image_base64: base64,
              include_matches: false
            }, { timeout: 5000 });
            if (dnaRes.data.success !== false) {
              setDnaAnalysis(dnaRes.data);
            }
          } catch (e) {
            console.error('DNA analysis fallback error:', e);
          }
        }
        clearTimeout(timeoutId);
        setDnaLoading(false);
        setVeinLoading(false);
      };
      
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('DNA fetch error:', err);
      clearTimeout(timeoutId);
      setDnaLoading(false);
      setVeinLoading(false);
    }
  };
  
  // Get images
  const images = material ? getMaterialImages(material) : [PLACEHOLDER_IMAGE];
  
  // Image navigation
  const handlePrevImage = () => setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  const handleNextImage = () => setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  
  // Actions
  const handleSave = () => {
    setIsSaved(!isSaved);
    toast.success(isSaved ? 'Rimosso dai salvati' : 'Salvato nei preferiti');
  };
  
  const handleShare = async () => {
    try {
      await navigator.share({
        title: material?.name,
        text: `Scopri ${material?.name} su SlabReality`,
        url: window.location.href
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiato!');
    }
  };
  
  const handleSREVisualize = () => {
    navigate(`/sre/project?material=${id}&name=${encodeURIComponent(material?.name || '')}`);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#C8A36A]/30 border-t-[#C8A36A] rounded-full animate-spin" />
          <span className="text-[#F5F1E8]/60 text-sm">Caricamento materiale...</span>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || !material) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif text-[#F5F1E8] mb-4">Materiale non trovato</h1>
          <button 
            onClick={() => navigate('/materials')}
            className="text-[#C8A36A] hover:underline"
          >
            Torna alla galleria
          </button>
        </div>
      </div>
    );
  }
  
  // Derive values
  const stoneType = material.stone_type || material.category || 'Pietra Naturale';
  const origin = material.origin || 'Italia';
  const description = material.description || `${material.name} è un materiale naturale di alta qualità, selezionato per la sua bellezza unica e le sue caratteristiche distintive. Ideale per progetti di architettura e design d'interni di lusso.`;
  
  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Custom Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1800px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-[#C8A36A] to-[#D4AF37] rounded-lg flex items-center justify-center">
              <Diamond className="w-4 h-4 text-black" />
            </div>
            <span className="text-[#F5F1E8] font-semibold tracking-tight">SlabReality</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/materials" className="text-sm text-[#F5F1E8]/70 hover:text-[#C8A36A] transition-colors">Materiali</Link>
            <Link to="/visual-search" className="text-sm text-[#F5F1E8]/70 hover:text-[#C8A36A] transition-colors">Visual Search</Link>
            <Link to="/inspiration" className="text-sm text-[#F5F1E8]/70 hover:text-[#C8A36A] transition-colors">Ispirazioni</Link>
            <Link to="/dna-compare" className="text-sm text-[#F5F1E8]/70 hover:text-[#C8A36A] transition-colors">DNA Compare</Link>
          </nav>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm text-[#C8A36A] border border-[#C8A36A]/30 rounded-full hover:bg-[#C8A36A]/10 transition-all"
            >
              Dashboard
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="pt-16">
        {/* Back Button */}
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#F5F1E8]/60 hover:text-[#C8A36A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Indietro</span>
          </button>
        </div>
        
        {/* 3-Column Luxury Layout */}
        <div className="max-w-[1800px] mx-auto px-6 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* LEFT COLUMN - Hero Image Gallery */}
            <div className="lg:col-span-5">
              <div className="sticky top-24 space-y-4">
                {/* Main Image */}
                <div 
                  ref={heroRef}
                  className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#0A0A0A] group cursor-zoom-in"
                  onMouseEnter={() => setImageHover(true)}
                  onMouseLeave={() => setImageHover(false)}
                  onClick={() => setShowFullscreen(true)}
                >
                  <img 
                    src={images[currentImageIndex]}
                    alt={material.name}
                    className={`w-full h-full object-cover transition-transform duration-700 ${imageHover ? 'scale-110' : 'scale-100'}`}
                    onError={handleImageError}
                  />
                  
                  {/* Cinematic Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Image Navigation */}
                  {images.length > 1 && (
                    <>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-black/60"
                      >
                        <ChevronLeft className="w-5 h-5 text-white" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-black/60"
                      >
                        <ChevronRight className="w-5 h-5 text-white" />
                      </button>
                    </>
                  )}
                  
                  {/* Expand Button */}
                  <button 
                    onClick={() => setShowFullscreen(true)}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-black/60"
                  >
                    <Maximize2 className="w-5 h-5 text-white" />
                  </button>
                  
                  {/* SRE Overlay CTA */}
                  <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleSREVisualize(); }}
                      className="w-full py-3 px-6 bg-[#C8A36A]/90 backdrop-blur-sm rounded-xl text-black font-medium flex items-center justify-center gap-2 hover:bg-[#C8A36A] transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Visualizza in ambiente
                    </button>
                  </div>
                </div>
                
                {/* Thumbnail Gallery */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                          idx === currentImageIndex ? 'border-[#C8A36A]' : 'border-transparent hover:border-[#C8A36A]/50'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" onError={handleImageError} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* CENTER COLUMN - Material Intelligence */}
            <div className="lg:col-span-4 space-y-8">
              {/* Category & Title */}
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#C8A36A] mb-3">{stoneType}</div>
                <h1 className="text-4xl md:text-5xl font-serif text-[#F5F1E8] mb-4 leading-tight">{material.name}</h1>
                <p className="text-[#F5F1E8]/65 leading-relaxed">{description}</p>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-3">
                <button 
                  onClick={handleSave}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-300 ${
                    isSaved 
                      ? 'bg-[#C8A36A]/20 border-[#C8A36A] text-[#C8A36A]' 
                      : 'border-white/10 text-[#F5F1E8]/70 hover:border-[#C8A36A]/50 hover:text-[#C8A36A]'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                  <span className="text-sm">{isSaved ? 'Salvato' : 'Salva'}</span>
                </button>
                <button 
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 text-[#F5F1E8]/70 hover:border-[#C8A36A]/50 hover:text-[#C8A36A] transition-all"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">Condividi</span>
                </button>
              </div>
              
              {/* DNA ANALYSIS SECTION */}
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C8A36A]/20 to-[#C8A36A]/5 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#C8A36A]" />
                  </div>
                  <h2 className="text-lg font-medium text-[#F5F1E8]">Material DNA Analysis</h2>
                </div>
                
                {dnaLoading ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-24 bg-white/[0.03] rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : dnaAnalysis ? (
                  <>
                    {/* DNA Cards Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <DNACard 
                        title="Tono Dominante" 
                        value={dnaAnalysis.dominant_tone || 'N/A'}
                        icon={Palette}
                      />
                      <DNACard 
                        title="Temperatura" 
                        value={dnaAnalysis.temperature || 'N/A'}
                        icon={Thermometer}
                      />
                      <DNACard 
                        title="Movimento Venature" 
                        value={dnaAnalysis.vein_movement_type || 'N/A'}
                        icon={Waves}
                      />
                      <DNACard 
                        title="Cristallizzazione" 
                        value={dnaAnalysis.crystal_presence || 'N/A'}
                        icon={Diamond}
                      />
                      <DNACard 
                        title="Mood Materiale" 
                        value={dnaAnalysis.material_mood || 'N/A'}
                        icon={Eye}
                      />
                      <DNACard 
                        title="Energia Superficie" 
                        value={dnaAnalysis.surface_energy || 'N/A'}
                        icon={Zap}
                      />
                    </div>
                    
                    {/* Color Palette */}
                    {dnaAnalysis.palette && dnaAnalysis.palette.length > 0 && (
                      <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5">
                        <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/50 mb-4">Palette Dominante</div>
                        <div className="flex items-center justify-center gap-4">
                          {dnaAnalysis.palette.slice(0, 7).map((color, idx) => (
                            <ColorSwatch key={idx} color={color} isMain={idx === 0} />
                          ))}
                        </div>
                        {dnaAnalysis.pantone_approx && (
                          <div className="mt-4 text-center">
                            <span className="text-xs text-[#F5F1E8]/40">{dnaAnalysis.pantone_approx}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Visual Metrics Bars */}
                    <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5 space-y-4">
                      <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/50 mb-2">Metriche Visive</div>
                      <DNAMetricBar label="Movimento Venature" value={Math.round(dnaAnalysis.vein_movement || 0)} icon={Waves} delay={100} />
                      <DNAMetricBar label="Livello Complessità" value={Math.round(dnaAnalysis.busy_level || 0)} icon={Grid3X3} delay={200} />
                      <DNAMetricBar label="Cristallizzazione" value={Math.round(dnaAnalysis.crystalization || 0)} icon={Diamond} delay={300} />
                      <DNAMetricBar label="Energia Contrasto" value={Math.round(dnaAnalysis.contrast_energy || 0)} icon={Activity} delay={400} />
                      <DNAMetricBar label="Calore" value={Math.round(dnaAnalysis.warmth || 0)} icon={Thermometer} delay={500} />
                    </div>
                    
                    {/* DNA Signature */}
                    {dnaAnalysis.dna_signature && (
                      <div className="bg-gradient-to-r from-[#C8A36A]/10 to-transparent border border-[#C8A36A]/20 rounded-xl p-5">
                        <div className="text-xs uppercase tracking-wider text-[#C8A36A]/70 mb-2">DNA Signature</div>
                        <div className="font-mono text-lg text-[#C8A36A]">{dnaAnalysis.dna_signature}</div>
                      </div>
                    )}
                    
                    {/* AI Interpretation */}
                    {dnaAnalysis.ai_interpretation && (
                      <div className="bg-white/[0.02] rounded-xl p-5 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-[#C8A36A]" />
                          <span className="text-xs uppercase tracking-wider text-[#F5F1E8]/50">AI Interpretation</span>
                        </div>
                        <p className="text-[#F5F1E8]/70 text-sm leading-relaxed italic">
                          "{dnaAnalysis.ai_interpretation}"
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white/[0.02] rounded-xl p-6 text-center">
                    <Sparkles className="w-8 h-8 text-[#C8A36A]/30 mx-auto mb-3" />
                    <p className="text-[#F5F1E8]/40 text-sm">DNA analysis non disponibile</p>
                  </div>
                )}
              </div>
              
              {/* VEIN INTELLIGENCE™ SECTION */}
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C8A36A]/20 to-[#C8A36A]/5 flex items-center justify-center">
                    <Compass className="w-4 h-4 text-[#C8A36A]" />
                  </div>
                  <h2 className="text-lg font-medium text-[#F5F1E8]">Vein Intelligence™</h2>
                </div>
                
                {veinLoading ? (
                  <div className="bg-white/[0.03] rounded-xl p-6 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-[#C8A36A]/30 border-t-[#C8A36A] rounded-full animate-spin" />
                    <span className="ml-3 text-sm text-[#F5F1E8]/60">Analyzing vein patterns...</span>
                  </div>
                ) : veinAnalysis ? (
                  <>
                    {/* Vein Direction & Visual Energy */}
                    <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5 space-y-5">
                      <div className="flex items-start justify-between">
                        <VeinDirectionIndicator 
                          angle={veinAnalysis.direction_angle || 0}
                          confidence={veinAnalysis.confidence || 60}
                          label={veinAnalysis.direction_label || 'Misto'}
                        />
                        <div className="text-right">
                          <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/40">Densità Venature</div>
                          <div className="text-2xl font-serif text-[#C8A36A]">{Math.round(veinAnalysis.vein_density || 0)}%</div>
                        </div>
                      </div>
                      
                      {/* Visual Energy Scale */}
                      <VisualEnergyScale level={veinAnalysis.visual_energy || 'Balanced'} />
                      
                      {/* Movement Metrics */}
                      <div className="grid grid-cols-2 gap-3 pt-2">
                        <div className="bg-white/[0.02] rounded-lg p-3">
                          <div className="text-[10px] uppercase text-[#F5F1E8]/40">Movimento</div>
                          <div className="text-sm text-[#F5F1E8]">{Math.round(veinAnalysis.movement_intensity || 0)}%</div>
                        </div>
                        <div className="bg-white/[0.02] rounded-lg p-3">
                          <div className="text-[10px] uppercase text-[#F5F1E8]/40">Continuità</div>
                          <div className="text-sm text-[#F5F1E8]">{Math.round(veinAnalysis.continuity || 0)}%</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Waterfall & Bookmatch Scores */}
                    <div className="grid grid-cols-1 gap-4">
                      <ContinuityScoreCard
                        title="Potenziale Waterfall"
                        score={veinAnalysis.waterfall_potential || 0}
                        recommendation={
                          veinAnalysis.waterfall_potential >= 70 
                            ? 'Eccellente per cascate continue su isole e piani di lavoro'
                            : veinAnalysis.waterfall_potential >= 50
                              ? 'Buon potenziale per applicazioni waterfall con attenzione ai raccordi'
                              : 'Considera orientamenti alternativi per continuità ottimale'
                        }
                        icon={Columns}
                      />
                      <ContinuityScoreCard
                        title="Potenziale Bookmatch"
                        score={veinAnalysis.bookmatch_potential || 0}
                        recommendation={
                          veinAnalysis.bookmatch_potential >= 70
                            ? 'Pattern ideale per specchiatura simmetrica'
                            : veinAnalysis.bookmatch_potential >= 50
                              ? 'Adatto per bookmatch con selezione attenta delle lastre'
                              : 'Pattern complesso - consigliata installazione sequenziale'
                        }
                        icon={LayoutGrid}
                      />
                    </div>
                    
                    {/* Recommended Alignment */}
                    <div className="bg-gradient-to-r from-[#C8A36A]/10 to-transparent border border-[#C8A36A]/20 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-[#C8A36A]/70">Orientamento Consigliato</div>
                          <div className="text-lg font-medium text-[#F5F1E8] mt-1">
                            {veinAnalysis.direction === 'horizontal' ? 'Orizzontale' : 
                             veinAnalysis.direction === 'vertical' ? 'Verticale' :
                             veinAnalysis.direction === 'diagonal' ? 'Diagonale' : 'Libero / Artistico'}
                          </div>
                        </div>
                        <div className={`text-xs px-3 py-1 rounded-full ${
                          veinAnalysis.continuity >= 70 ? 'bg-green-500/20 text-green-400' :
                          veinAnalysis.continuity >= 50 ? 'bg-[#C8A36A]/20 text-[#C8A36A]' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {veinAnalysis.continuity >= 70 ? 'Basso' : veinAnalysis.continuity >= 50 ? 'Medio' : 'Alto'} Rischio
                        </div>
                      </div>
                    </div>
                    
                    {/* Application Recommendations */}
                    <div className="bg-white/[0.02] rounded-xl p-5 border border-white/5">
                      <div className="flex items-center gap-2 mb-4">
                        <Target className="w-4 h-4 text-[#C8A36A]" />
                        <span className="text-xs uppercase tracking-wider text-[#F5F1E8]/50">Ideal Applications</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {/* Show applications based on scores */}
                        {veinAnalysis.waterfall_score > 60 && <ApplicationBadge label="Waterfall Island" recommended={veinAnalysis.waterfall_score > 75} />}
                        {veinAnalysis.bookmatch_score > 60 && <ApplicationBadge label="Feature Wall" recommended={veinAnalysis.bookmatch_score > 75} />}
                        {(veinAnalysis.movement_intensity || 0) < 40 && <ApplicationBadge label="Spa Environment" recommended />}
                        {(veinAnalysis.movement_intensity || 0) > 50 && <ApplicationBadge label="Statement Kitchen" recommended />}
                        {veinAnalysis.flow_uniformity > 60 && <ApplicationBadge label="Hospitality" recommended={veinAnalysis.flow_uniformity > 75} />}
                        {veinAnalysis.vein_density > 50 && <ApplicationBadge label="Luxury Retail" recommended={false} />}
                        {veinAnalysis.vein_density < 30 && <ApplicationBadge label="Bathroom" recommended />}
                        {(veinAnalysis.movement_intensity || 0) > 60 && <ApplicationBadge label="Fireplace Surround" recommended={false} />}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white/[0.02] rounded-xl p-6 text-center">
                    <Compass className="w-8 h-8 text-[#C8A36A]/30 mx-auto mb-3" />
                    <p className="text-[#F5F1E8]/40 text-sm">Vein analysis non disponibile</p>
                  </div>
                )}
              </div>
              
              {/* CTA Section */}
              <div className="space-y-3 pt-4">
                <button 
                  onClick={handleSREVisualize}
                  className="w-full py-4 px-6 bg-gradient-to-r from-[#C8A36A] to-[#D4AF37] rounded-xl text-black font-medium flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#C8A36A]/20 transition-all duration-300"
                >
                  <Play className="w-5 h-5" />
                  Visualizza in SRE
                </button>
                
                <div className="grid grid-cols-2 gap-3">
                  <button className="py-3 px-4 bg-white/[0.03] border border-white/10 rounded-xl text-[#F5F1E8] text-sm hover:border-[#C8A36A]/30 hover:bg-white/[0.05] transition-all flex items-center justify-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Richiedi consulenza
                  </button>
                  <button className="py-3 px-4 bg-white/[0.03] border border-white/10 rounded-xl text-[#F5F1E8] text-sm hover:border-[#C8A36A]/30 hover:bg-white/[0.05] transition-all flex items-center justify-center gap-2">
                    <Layers className="w-4 h-4" />
                    Confronta
                  </button>
                </div>
                
                <button className="w-full py-3 px-4 border border-[#C8A36A]/30 rounded-xl text-[#C8A36A] text-sm hover:bg-[#C8A36A]/10 transition-all flex items-center justify-center gap-2">
                  <Camera className="w-4 h-4" />
                  Richiedi foto lastre reali
                </button>
              </div>
            </div>
            
            {/* RIGHT COLUMN - Advisor Ecosystem */}
            <div className="lg:col-span-3 space-y-6">
              {/* Location Card */}
              <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#C8A36A]/10 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#C8A36A]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/50">Origine</div>
                    <div className="text-sm text-[#F5F1E8]">{origin}</div>
                  </div>
                </div>
                <div className="h-32 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
                  <span className="text-[#F5F1E8]/30 text-xs">Mappa origine</span>
                </div>
              </div>
              
              {/* Nearby Showrooms */}
              <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#C8A36A]" />
                    <span className="text-sm text-[#F5F1E8]">Showroom vicini</span>
                  </div>
                  <Link to="/retail-advisor" className="text-xs text-[#C8A36A] hover:underline">Vedi tutti</Link>
                </div>
                <div className="space-y-2">
                  <AdvisorCard name="Margraf Showroom Milano" role="Showroom Ufficiale" distance="5.2 km" />
                  <AdvisorCard name="Stone Gallery Roma" role="Premium Retailer" distance="12 km" />
                </div>
              </div>
              
              {/* Advisors - Professional Network - AIL INTELLIGENT MATCHING */}
              <AdvisorSection 
                advisors={matchedAdvisors}
                isLoading={advisorsLoading}
                materialName={material?.name}
                onAdvisorClick={(advisor) => {
                  // Navigate to advisor profile or show details modal
                  console.log('[AIL] Advisor clicked:', advisor.full_name);
                  toast.info(`Profilo ${advisor.full_name} in arrivo...`);
                }}
                onContactClick={() => {
                  // Open consultation request modal
                  toast.info('Richiesta consulenza - coming soon');
                }}
              />
              
              {/* Partner Availability - REAL MODEL */}
              <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-4 h-4 text-[#C8A36A]" />
                  <span className="text-sm text-[#F5F1E8]">Disponibilità</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <span className="text-[#F5F1E8]/80 text-sm">Disponibile tramite rete partner</span>
                  </div>
                  <p className="text-[#F5F1E8]/50 text-xs">
                    Quantità e tempistiche specifiche saranno fornite dopo la consulenza con un advisor.
                  </p>
                </div>
              </div>
              
              {/* Delivery Info - REALISTIC */}
              <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-4 h-4 text-[#C8A36A]" />
                  <span className="text-sm text-[#F5F1E8]">Consegna</span>
                </div>
                <div className="text-lg font-medium text-[#C8A36A]">Gestita dal partner selezionato</div>
                <div className="text-xs text-[#F5F1E8]/50 mt-2">
                  Le tempistiche di consegna variano in base al partner, alla disponibilità e alla destinazione.
                </div>
              </div>
              
              {/* Contact Quick Actions */}
              <div className="flex gap-2">
                <button className="flex-1 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-[#F5F1E8]/70 hover:border-[#C8A36A]/30 transition-all flex items-center justify-center gap-2 text-sm">
                  <Phone className="w-4 h-4" />
                  Chiama
                </button>
                <button className="flex-1 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-[#F5F1E8]/70 hover:border-[#C8A36A]/30 transition-all flex items-center justify-center gap-2 text-sm">
                  <Mail className="w-4 h-4" />
                  Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Fullscreen Gallery Modal */}
      {showFullscreen && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center"
          onClick={() => setShowFullscreen(false)}
        >
          <button 
            onClick={() => setShowFullscreen(false)}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <span className="text-white text-2xl">&times;</span>
          </button>
          
          <img 
            src={images[currentImageIndex]}
            alt={material.name}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {images.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}
          
          {/* Image Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-[1800px] mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C8A36A] to-[#D4AF37] rounded-lg flex items-center justify-center">
                <Diamond className="w-4 h-4 text-black" />
              </div>
              <span className="text-[#F5F1E8]/50 text-sm">SlabReality © 2026</span>
            </div>
            <div className="text-[#F5F1E8]/30 text-xs">Material Intelligence Platform</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MaterialDetailLuxury;
